import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRoutesFromChildren,
  BrowserRouter as Router,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { Branding } from '@companion-professional/branding';
import '@companion-professional/branding/shepherd/fonts.css';
import { WindowWithCheckupSettings } from './types/global';
import { App } from './App';
import './tailwind.css';

declare const __VERSION__: string;
declare const __COMMIT_HASH__: string;
declare let window: WindowWithCheckupSettings;

Sentry.init({
  environment: window.settings.env,
  dsn: window.settings.sentryDsn,
  release: `${__VERSION__}-${__COMMIT_HASH__}`,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],

  // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a
  // lower rate in production.
  replaysSessionSampleRate: 0.1,

  // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where
  // errors occur.
  replaysOnErrorSampleRate: 1.0,

  // tracesSampleRate is to 1.0 to capture 100% of transactions for performance monitoring.  We can adjust this value
  // later if we want to sample transactions.
  tracesSampleRate: 1.0,

  // tracePropagationTargets controls which URLs distributed tracing should be enabled on.
  tracePropagationTargets: ['localhost', /^https:\/\/checkup\.(.*\.)?companionpro\.biz/i]
});

// PostHog is used for analytics, event tracking and feature flags.  The following are the options that are passed to
// the PostHogProvider.
const posthogOptions: Partial<PostHogConfig> = {
  api_host: window.settings.posthogHost,

  //  This is to prevent capturing events while developing locally (essentially, this calls the
  //  posthog.opt_out_capturing() method in the PostHogProvider when opt_out_capturing_by_default is set to true).
  opt_out_capturing_by_default: window.settings.env === 'development'
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Turning off retries for now.  We can revisit this later if we want to add retries.
      retry: false
    }
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary>
      <PostHogProvider apiKey={window.settings.posthogPublicKey} options={posthogOptions}>
        <QueryClientProvider client={queryClient}>
          <Branding>
            <Router>
              <App />
            </Router>
          </Branding>
        </QueryClientProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
